/* eslint-disable no-unused-vars */
import Requestor from '../Utils/fetchHTTP/Requestor';
import { cleanEmptyValues } from '../Utils/utils';
import { keysToSnakeCase } from '../Utils/formats';
import AnalyticsEvent from './analytics/AnalyticsEvent';
import Auth from '../Utils/Auth/auth';
import {
  ACCOUNT_MANAGER,
  GENERAL_ADMIN,
  STORE_ADMIN,
} from '../Utils/constants';

const getTransactions = async ({ paymentScheduleId = '', ...parameters }) => {
  const defaultResponse = {
    page: 1,
    page_size: 1,
    total_pages: 1,
    total_records: 0,
    transactions: [],
  };
  const params = parameters
    ? { params: keysToSnakeCase(cleanEmptyValues(parameters)) }
    : {};
  try {
    const response = await Requestor().getRequest({
      method: 'GET',
      url: `/api/v1/protected/merchants_payments/payment_schedules/${paymentScheduleId}/transactions`,
      ...params,
    });

    if (response && response.data && response.data) {
      return response.data;
    }

    return defaultResponse;
  } catch (error) {
    console.error(error);
    return defaultResponse;
  }
};

const getTransactionById = async ({
  paymentScheduleId = '',
  transactionId = '',
}) => {
  const defaultResponse = {
    page: 1,
    page_size: 1,
    total_pages: 1,
    total_records: 0,
    transactions: [],
  };

  try {
    const response = await Requestor().getRequest({
      method: 'GET',
      url: `/api/v1/protected/merchants_payments/payment_schedules/${paymentScheduleId}/transactions`,
      params: {
        transaction_id: transactionId,
      },
    });

    if (response && response.data && response.data) {
      return response.data;
    }

    return defaultResponse;
  } catch (error) {
    console.error(error);
    return defaultResponse;
  }
};

const getPaymentDateSchedules = async parameters => {
  try {
    const params = parameters
      ? keysToSnakeCase(cleanEmptyValues(parameters))
      : {};
    const response = await Requestor().getRequest({
      method: 'GET',
      url: '/api/v1/protected/merchants_payments/payment_schedules',
      params: {
        status: 'completed',
        ...params,
      },
    });

    if (response && response.data) {
      return response.data;
    }

    return [];
  } catch (error) {
    console.error(error);
    return [];
  }
};

const getSummaryPaymentSchedule = async parameters => {
  try {
    const response = await Requestor().getRequest({
      method: 'GET',
      url: `/api/v1/protected/merchants_payments/payment_schedules/${parameters.paymentScheduleId}`,
    });

    if (response && response.data) {
      return response.data;
    }

    return {
      details: {},
    };
  } catch (error) {
    console.error(error);
    return {
      details: {},
    };
  }
};

const exportPaymentReport = async ({ paymentScheduleId, ...rest }) => {
  try {
    const response = await Requestor().getRequest({
      method: 'GET',
      url: `/api/v1/protected/merchants_payments/payment_schedules/${paymentScheduleId}/transactions/export`,
      params: keysToSnakeCase(cleanEmptyValues(rest)),
    });

    if (response && response.data) {
      const data = JSON.parse(response.data);
      const blob = new Blob([data], { type: 'text/csv; charset=utf-18' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = 'payments.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    }

    return null;
  } catch (error) {
    console.error(error);
    return null;
  }
};

const getMerchantUser = async userId => {
  try {
    const response = await Requestor().getRequest({
      method: 'GET',
      url: `/api/protected/merchant_users/${userId}`,
    });

    if (response && response.data && response.data.user) {
      return response.data.user;
    }

    throw response;
  } catch (error) {
    AnalyticsEvent().errorTrack({
      errorType: 'get-merchant-user-error',
      errorDescription: error,
    });

    if (error?.response?.status === 404 || error?.response?.status === 400) {
      return null;
    }

    return {
      first_name: '',
      last_name: '',
      maiden_name: '',
      email: '',
      user_code: '',
      status: '',
      role_id: '',
      stores: [],
    };
  }
};

const getStoresLight = async merchantId => {
  let parameters = {
    disable_pagination: true,
    light_response: true,
  };

  if (merchantId) {
    parameters = {
      ...parameters,
      merchant_id: merchantId,
    };
  }

  try {
    const response = await Requestor().getRequest({
      method: 'GET',
      url: '/api/protected/stores',
      params: parameters,
    });

    if (response && response.data && response.data.stores) {
      return response.data.stores;
    }

    return [];
  } catch (error) {
    console.error(error);
    return [];
  }
};

const getPaymentsMovementsAggregate = async parameters => {
  const defaultResponse = {
    page: 1,
    page_size: 1,
    total_pages: 1,
    total_records: 0,
    payments: [],
    summary: {
      payments_total: 0.0,
      payments_count: 0,
      cancelations_total: 0.0,
      cancelations_count: 0,
    },
  };

  try {
    const response = await Requestor().getRequest({
      method: 'GET',
      url: '/api/protected/merchant_sales/payments_movements_aggregates',
      params: {
        order_by: 'created_at',
        ...keysToSnakeCase(cleanEmptyValues(parameters)),
      },
    });

    if (response && response.data) {
      return {
        page: response.data.pagination.actual_page,
        page_size: parameters.pageSize,
        total_pages: response.data.pagination.total_pages,
        total_records: response.data.pagination.total_items,
        payments: response.data.payments,
        summary: response.data.summary,
      };
    }

    return defaultResponse;
  } catch (error) {
    console.error(error);
    return defaultResponse;
  }
};

const exportTransactionReport = async parameters => {
  const { merchant_id, ...rest } = parameters;

  try {
    const response = await Requestor().getRequest({
      method: 'GET',
      url: '/api/protected/merchant_sales/payments_movements_aggregates/export',
      responseType: 'blob',
      params: {
        order_by: 'payment_created_at_desc',
        merchant_id: merchant_id,
        ...keysToSnakeCase(cleanEmptyValues(rest)),
      },
    });

    if (response) {
      const url = window.URL.createObjectURL(response);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = 'payments.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    }

    return null;
  } catch (error) {
    console.error(error);
    return null;
  }
};

const getOrders = async parameters => {
  const defaultResponse = {
    page: 1,
    page_size: 1,
    total_pages: 1,
    total_records: 0,
    orders: [],
  };

  try {
    const response = await Requestor().getRequest({
      method: 'GET',
      url: '/api/protected/orders',
      params: keysToSnakeCase(cleanEmptyValues(parameters)),
    });

    if (response && response.data) {
      return {
        page: response.data.pagination.actual_page,
        page_size: parameters.pageSize || 20,
        total_pages: response.data.pagination.total_pages,
        total_records: response.data.pagination.total_items,
        orders: response.data.orders,
      };
    }

    return defaultResponse;
  } catch (error) {
    AnalyticsEvent().errorTrack({
      errorType: 'payment-hisotory-get-orders-error',
      errorDescription: error,
    });
    console.error(error);
    return defaultResponse;
  }
};

const getPayments = async parameters => {
  const params = parameters
    ? keysToSnakeCase(cleanEmptyValues(parameters))
    : {};
  const response = await Requestor().getRequest({
    method: 'GET',
    url: '/api/protected/merchant_sales/payments_movements_aggregates',
    params: {
      ...params,
      order_by: 'created_at',
    },
  });

  return response.data;
};

const getOrderDetail = async (orderId, storeId) => {
  const response = await Requestor().getRequest({
    method: 'GET',
    url: `/api/protected/orders/${orderId}?store_id=${storeId}`,
  });

  if (response && response.data && response.data.order) {
    return response.data.order;
  }

  throw response;
};

const getPayment = async paymentId => {
  const response = await Requestor().getRequest({
    method: 'GET',
    url: `/api/protected/merchant_sales/payments/${paymentId}`,
  });

  return response;
};

const requestOrderRefund = async refund => {
  return await Requestor().getRequest({
    method: 'POST',
    url: '/api/protected/refunds',
    data: refund,
  });
};

const getHomeAssets = async merchant => {
  try {
    let channel = '';
    const hasOnlineChannel = !!merchant?.channels?.find(
      channel => channel.channel === 'online' && channel.active
    );
    const hasInStoreChannel = !!merchant?.channels?.find(
      channel => channel.channel === 'instore' && channel.active
    );

    if (hasOnlineChannel && !hasInStoreChannel) {
      channel = 'online';
    } else if (!hasOnlineChannel && hasInStoreChannel) {
      channel = 'ecommerce';
    }

    const response = await Requestor().getRequest({
      method: 'GET',
      url: '/api/v1/protected/assets',
      params: { channel: channel },
    });

    if (response && response.data) {
      return response.data;
    }

    throw response;
  } catch (error) {
    AnalyticsEvent().errorTrack({
      errorType: 'get-home-assets-error',
      errorDescription: error,
    });
    console.log(error);
    return [];
  }
};

const getCategories = async () => {
  try {
    const response = await Requestor().getRequest({
      method: 'GET',
      url: '/api/protected/stores/categories',
    });

    if (response && response.data) {
      return response.data;
    }

    throw response;
  } catch (error) {
    AnalyticsEvent().errorTrack({
      errorType: 'get-home-assets-error',
      errorDescription: error,
    });
    console.log(error);
    return [];
  }
};

const getStores = async (
  search,
  page = 1,
  limit = 9,
  filterParams = {},
  getAllStores = false
) => {
  const storeName = search && search.length > 4 ? search : null;
  const skipPagination = getAllStores;

  const response = await Requestor().getRequest({
    method: 'GET',
    url: '/api/protected/stores',
    params: {
      store_name: storeName,
      page,
      limit,
      store_id: filterParams['storeId'],
      active: filterParams['active'],
      merchant_id: filterParams['merchant_id'],
      skip_pagination: skipPagination,
    },
  });

  return response;
};

const getStoreById = async storeId => {
  const response = await Requestor().getRequest({
    method: 'GET',
    url: `/api/protected/stores/${storeId}`,
  });

  if (response && response.data && response.data.store) {
    return response.data.store;
  }

  throw response;
};

const getStoreUsers = async ({
  storeId,
  page = 1,
  limit = 20,
  showUsersStore = true,
  module = '',
}) => {
  const defaultResponse = {
    pagination: {
      actual_page: 1,
      total_items: 0,
      total_pages: 1,
    },
    summary: [],
    users: [],
  };

  try {
    const response = await Requestor().getRequest({
      method: 'GET',
      url: '/api/protected/merchant_users',
      params: {
        store_ids: [storeId],
        page: page,
        limit: limit,
        show_users_without_stores: showUsersStore,
      },
    });

    if (response && response.data) {
      return response.data;
    }

    throw response;
  } catch (error) {
    AnalyticsEvent().errorTrack({
      errorType: `${module}-get-store-users-error`,
      errorDescription: error,
    });
    console.log(error);
    return defaultResponse;
  }
};

const getMerchantUsers = async ({
  search,
  page = 1,
  limit = 9,
  filterParams = {},
}) => {
  const { employee } = Auth.userData();
  const defaultResponse = {
    status: 'error',
    data: {
      pagination: {
        actual_page: 1,
        total_items: 0,
        total_pages: 1,
      },
      summary: [],
      users: [],
    },
  };

  if (
    ![STORE_ADMIN, GENERAL_ADMIN, ACCOUNT_MANAGER].includes(
      employee?.role?.name
    )
  ) {
    return defaultResponse;
  }

  const response = await Requestor().getRequest({
    method: 'GET',
    url: '/api/protected/merchant_users',
    params: {
      search_data:
        search && search.length > 4 ? encodeURIComponent(search) : null,
      page,
      limit,
      merchant_id: filterParams?.['merchant_id'] ?? null,
    },
  });

  return response;
};

const getMerchantRoles = async merchantId => {
  try {
    const response = await Requestor().getRequest({
      method: 'GET',
      url: '/api/protected/roles',
      params: {
        merchant_id: merchantId,
      },
    });

    if (response && response.data && response.data.roles) {
      return response.data.roles;
    }

    throw response;
  } catch (error) {
    AnalyticsEvent().errorTrack({
      errorType: 'get-merchant-roles-error',
      errorDescription: error,
    });
    console.log(error);

    return [];
  }
};

const getZipCodeInfo = async zipCode => {
  try {
    const response = await Requestor().getRequest({
      method: 'GET',
      url: '/api/protected/stores/zipcodes',
      params: {
        zip_code: zipCode,
      },
    });

    if (response && response.zip_codes) {
      return response.zip_codes;
    }

    throw response;
  } catch (error) {
    AnalyticsEvent().errorTrack({
      errorType: 'get-zip-code-info-error',
      errorDescription: error,
    });
    console.log(error);

    return [];
  }
};

const getMerchantsByTerm = async term => {
  try {
    const response = await Requestor().getRequest({
      method: 'GET',
      url: '/api/protected/merchants',
      params: {
        search: term,
      },
    });

    return response;
  } catch (error) {
    AnalyticsEvent().errorTrack({
      errorType: 'get-merchant-by-term-error',
      errorDescription: error,
    });
    console.log(error);

    return [];
  }
};

const requestKueskiCouponValidation = async kueskiCoupon => {
  return await Requestor().getRequest({
    method: 'GET',
    url: `/api/protected/instore/codes/${kueskiCoupon}`,
  });
};

const getStaticCodeByStore = async storeId => {
  return await Requestor().getRequest({
    method: 'GET',
    url: `/api/protected/instore/stores/${storeId}/qr`,
  });
};

const getMallList = async cities => {
  return await Requestor().getRequest({
    method: 'GET',
    url: '/api/protected/malls',
    params: { cities },
  });
};

const saveLogbehavior = async ({ userId, log_behavior_data }) => {
  try {
    return await Requestor().getRequest({
      method: 'POST',
      url: '/api/protected/log',
      data: {
        user_id: userId,
        log_behavior: log_behavior_data,
      },
    });
  } catch (error) {
    AnalyticsEvent().errorTrack({
      errorType: 'save-log-behavior-error',
      errorDescription: error,
    });
    console.log(error);
  }
};

const createOrder = async order => {
  return await Requestor().getRequest({
    method: 'POST',
    url: '/api/protected/orders',
    data: order,
  });
};

const createInstorePayment = async order => {
  return await Requestor().getRequest({
    method: 'POST',
    url: '/api/protected/instore/payments',
    data: order,
  });
};

const getBulkRefunds = async parameters => {
  const defaultResponse = {
    page: 1,
    page_size: 1,
    total_pages: 1,
    total_records: 0,
    refunds: [],
  };

  try {
    const params = parameters
      ? keysToSnakeCase(cleanEmptyValues(parameters))
      : {};
    const response = await Requestor().getRequest({
      method: 'GET',
      url: '/api/protected/v1/refunds/bulk',
      params: params,
    });

    if (!response || !response.data) {
      return defaultResponse;
    }

    return {
      page: response.data.pagination.actual_page,
      page_size: parameters.pageSize || 20,
      total_pages: response.data.pagination.total_pages,
      total_records: response.data.pagination.total_items,
      refunds: response.data.files ? response.data.files : [],
    };
  } catch (error) {
    AnalyticsEvent().errorTrack({
      errorType: 'get-bulk-refunds-error',
      errorDescription: error,
    });
    console.error(error);
    return defaultResponse;
  }
};

const bulkRefunds = async ({ merchantId, jsonTemplate }) => {
  const response = await Requestor().getRequest({
    method: 'POST',
    url: '/api/protected/v1/refunds/bulk',
    data: {
      merchant_id: merchantId,
      transactions: jsonTemplate,
    },
  });

  if (!response || !response.data) {
    throw new Error('Missing data response');
  }

  return response.data;
};

export default function MerchantPaymentsService() {
  return Object.create({
    bulkRefunds,
    getBulkRefunds,
    getOrderDetail,
    getTransactions,
    getPaymentDateSchedules,
    getSummaryPaymentSchedule,
    getTransactionById,
    exportPaymentReport,
    getMerchantUser,
    getStoresLight,
    getPaymentsMovementsAggregate,
    exportTransactionReport,
    getOrders,
    getPayments,
    getPayment,
    requestOrderRefund,
    getHomeAssets,
    getCategories,
    getStores,
    getStoreById,
    getStoreUsers,
    getMerchantUsers,
    getMerchantRoles,
    getZipCodeInfo,
    getMerchantsByTerm,
    requestKueskiCouponValidation,
    getMallList,
    saveLogbehavior,
    createOrder,
    createInstorePayment,
    getStaticCodeByStore,
  });
}
